import { useMemo, useState } from 'react';
import { useDataStore } from '../stores/dataStore';
import { useSelectionStore } from '../stores/selectionStore';
import { useIsMobile } from '../hooks/useIsMobile';
import type { Company, Ranking, FinancialRecord } from '../types';
import DecisionBar from './skilltree/DecisionBar';

interface CompanyRankingProps {
  chain: string;
}

type TabKey = 'priority' | 'tqs' | 'mcs';
type MarketFilter = 'all' | 'A股' | '港股通' | '美股';
type QuadrantFilter = 'all' | '核心主攻' | '质量等待' | '弹性交易' | '低优先级';
type ModeFilter = 'all' | 'A' | 'B' | 'AB';
type SortField = 'priority' | 'tqs' | 'mcs' | 'ai' | 'gain3y';

interface MergedRow {
  company_id: string;
  company: Company | undefined;
  ranking: Ranking;
  financial: FinancialRecord | undefined;
}

const QUADRANT_COLORS: Record<string, { bg: string; text: string }> = {
  '核心主攻': { bg: '#dcfce7', text: '#166534' },
  '质量等待': { bg: '#dbeafe', text: '#1e40af' },
  '弹性交易': { bg: '#ffedd5', text: '#9a3412' },
  '低优先级': { bg: '#f3f4f6', text: '#6b7280' },
};

const MODE_ICONS: Record<string, string> = {
  A: '📊',
  B: '💎',
  AB: '⚡',
};

const PAGE_SIZE = 20;

export default function CompanyRanking({ chain }: CompanyRankingProps) {
  const { rankings, financials, companyIndex, index } = useDataStore();
  const selectedNodeId = useSelectionStore(s => s.selectedNodeId);
  const selectedCompanyId = useSelectionStore(s => s.selectedCompanyId);
  const selectCompany = useSelectionStore(s => s.selectCompany);

  const [tab, setTab] = useState<TabKey>('priority');
  const [marketFilter, setMarketFilter] = useState<MarketFilter>('all');
  const [quadrantFilter, setQuadrantFilter] = useState<QuadrantFilter>('all');
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('priority');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(0);
  const isMobile = useIsMobile();

  const latestFinancials = useMemo(() => {
    const map: Record<string, FinancialRecord> = {};
    for (const f of financials) {
      if (!map[f.company_id]) map[f.company_id] = f;
    }
    return map;
  }, [financials]);

  const merged: MergedRow[] = useMemo(() => {
    return rankings.map(r => ({
      company_id: r.company_id,
      company: companyIndex.get(r.company_id),
      ranking: r,
      financial: latestFinancials[r.company_id],
    }));
  }, [rankings, companyIndex, latestFinancials]);

  const filtered = useMemo(() => {
    let result = merged;

    // Chain filter
    if (chain !== 'all') {
      result = result.filter(r => r.company?.chains?.includes(chain));
    }

    // Market filter
    if (marketFilter === 'A股') {
      result = result.filter(r => r.company?.exchange === 'A股');
    } else if (marketFilter === '港股通') {
      result = result.filter(r => r.company?.investment_access?.hk_connect === true);
    } else if (marketFilter === '美股') {
      result = result.filter(r => r.company?.exchange === '美股');
    }

    // Quadrant filter
    if (quadrantFilter !== 'all') {
      result = result.filter(r => r.ranking.quadrant === quadrantFilter);
    }

    // Mode filter
    if (modeFilter !== 'all') {
      result = result.filter(r => r.ranking.mode_label === modeFilter);
    }

    // Search
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(r =>
        r.company?.company?.toLowerCase().includes(q) ||
        r.company_id.toLowerCase().includes(q) ||
        r.company?.ticker?.toLowerCase().includes(q)
      );
    }

    // Sort
    const getVal = (r: MergedRow): number => {
      switch (sortField) {
        case 'priority': return r.ranking.priority_score ?? 0;
        case 'tqs': return r.ranking.tqs_score ?? 0;
        case 'mcs': return r.ranking.mcs_score ?? 0;
        case 'ai': return r.company?.ai_revenue_pct ?? 0;
        case 'gain3y': return r.company?.price_gain_3y ?? -Infinity;
        default: return 0;
      }
    };
    result.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      return sortAsc ? va - vb : vb - va;
    });

    return result;
  }, [merged, chain, marketFilter, quadrantFilter, modeFilter, search, sortField, sortAsc]);

  // 联动高亮：选中节点时，派生关联公司集合（不入 store，本地派生）
  const relatedCompanyIds = useMemo(() => {
    if (!selectedNodeId || !index?.nodeCompaniesMap) return null;
    const ids = index.nodeCompaniesMap[selectedNodeId];
    return ids && ids.length > 0 ? new Set(ids) : null;
  }, [selectedNodeId, index]);

  const hasSelection = !!(selectedNodeId || selectedCompanyId);

  // 自动翻页：选中节点/公司时，跳到关联公司所在页
  const filterKey = `${chain}|${marketFilter}|${quadrantFilter}|${modeFilter}|${search}|${tab}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  const [prevSelectionKey, setPrevSelectionKey] = useState('');
  const selectionKey = `${selectedNodeId ?? ''}|${selectedCompanyId ?? ''}`;
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setPage(0);
  }
  if (prevSelectionKey !== selectionKey) {
    setPrevSelectionKey(selectionKey);
    // 优先翻页到选中公司，其次翻页到节点关联公司
    const targetId = selectedCompanyId ?? (relatedCompanyIds ? Array.from(relatedCompanyIds)[0] : null);
    if (targetId) {
      const idx = filtered.findIndex(r => r.company_id === targetId);
      if (idx >= 0) {
        const targetPage = Math.floor(idx / PAGE_SIZE);
        if (targetPage !== page) setPage(targetPage);
      }
    }
  }

  const [prevTab, setPrevTab] = useState(tab);
  if (prevTab !== tab) {
    setPrevTab(tab);
    if (tab === 'priority') setSortField('priority');
    else if (tab === 'tqs') setSortField('tqs');
    else if (tab === 'mcs') setSortField('mcs');
    setSortAsc(false);
  }

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(false); }
  };

  const formatGain = (val: number | undefined): string => {
    if (val === undefined || val === null) return '-';
    return `${val >= 0 ? '+' : ''}${val.toFixed(1)}%`;
  };

  const gainColor = (val: number | undefined): string => {
    if (val === undefined || val === null) return '#6b7280';
    return val >= 0 ? '#16a34a' : '#dc2626';
  };

  // Mobile card layout
  if (isMobile) {
    return (
      <div style={{ padding: '12px', overflow: 'auto', height: '100%', background: '#f8fafc', color: '#1f2937' }}>
        {/* Mobile filters in dropdown */}
        <MobileFilterBar
          marketFilter={marketFilter} setMarketFilter={setMarketFilter}
          quadrantFilter={quadrantFilter} setQuadrantFilter={setQuadrantFilter}
          modeFilter={modeFilter} setModeFilter={setModeFilter}
          search={search} setSearch={setSearch}
        />

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
          {([
            { key: 'priority' as TabKey, label: '综合榜' },
            { key: 'tqs' as TabKey, label: '质量榜' },
            { key: 'mcs' as TabKey, label: '动量榜' },
          ]).map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              flex: 1, padding: '8px', borderRadius: '6px', fontSize: '13px',
              border: tab === t.key ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: tab === t.key ? '#eff6ff' : 'white', cursor: 'pointer', fontWeight: 600,
            }}>
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
          共 {filtered.length} 家 · 第 {page + 1}/{totalPages || 1} 页
        </div>

        {/* Cards */}
        {paged.map((r, i) => {
          const isSelected = selectedCompanyId === r.company_id;
          const isRelated = !!(relatedCompanyIds?.has(r.company_id));
          const cardBg = isSelected ? '#fef3c7' : isRelated ? '#fffbeb' : 'white';
          const cardOpacity = hasSelection && !isSelected && !isRelated ? 0.4 : 1;
          const cardBorder = isSelected ? '2px solid #f59e0b' : isRelated ? '2px solid #fbbf24' : '1px solid #e5e7eb';
          return (
          <div key={r.company_id}
            onClick={() => selectCompany(selectedCompanyId === r.company_id ? null : r.company_id)}
            style={{
            background: cardBg, border: cardBorder, borderRadius: '8px',
            padding: '12px', marginBottom: '8px', opacity: cardOpacity, cursor: 'pointer',
            transition: 'background 0.15s, opacity 0.15s',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <div>
                <span style={{ fontWeight: 700, fontSize: '14px' }}>{page * PAGE_SIZE + i + 1}. {r.company?.company || r.company_id}</span>
                <span style={{ color: '#6b7280', fontSize: '11px', marginLeft: '6px', fontFamily: 'monospace' }}>{r.company?.ticker || ''}</span>
              </div>
              {r.ranking.quadrant && (
                <span style={{
                  padding: '2px 8px', borderRadius: '4px', fontSize: '10px',
                  background: QUADRANT_COLORS[r.ranking.quadrant]?.bg || '#f3f4f6',
                  color: QUADRANT_COLORS[r.ranking.quadrant]?.text || '#6b7280',
                  fontWeight: 600,
                }}>
                  {r.ranking.quadrant}
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '4px', fontSize: '12px' }}>
              <div>
                <span style={{ color: '#6b7280' }}>综合P</span><br />
                <span style={{ fontWeight: 700 }}>{(r.ranking.priority_score ?? 0).toFixed(1)}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>TQS</span><br />
                <span style={{ fontWeight: 600 }}>{(r.ranking.tqs_score ?? 0).toFixed(1)}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>MCS</span><br />
                <span style={{ fontWeight: 600 }}>{(r.ranking.mcs_score ?? 0).toFixed(1)}</span>
              </div>
              <div>
                <span style={{ color: '#6b7280' }}>3年涨幅</span><br />
                <span style={{ fontWeight: 600, color: gainColor(r.company?.price_gain_3y) }}>
                  {formatGain(r.company?.price_gain_3y)}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', fontSize: '11px', color: '#6b7280' }}>
              <span>{MODE_ICONS[r.ranking.mode_label ?? ''] || ''} {r.ranking.mode_label === 'A' ? '财报验证' : r.ranking.mode_label === 'B' ? '稀缺爆发' : r.ranking.mode_label === 'AB' ? '混合' : r.ranking.mode_label || '-'}</span>
              <span>·</span>
              <span>AI {r.company?.ai_revenue_pct?.toFixed(0) ?? '-'}%</span>
              <span>·</span>
              <span>{r.company?.exchange || ''}</span>
            </div>
          </div>
          );
        })}

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '12px' }}>
          <button disabled={page === 0} onClick={() => setPage(page - 1)} style={{
            padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db',
            background: page === 0 ? '#f9fafb' : 'white', cursor: page === 0 ? 'not-allowed' : 'pointer',
            fontSize: '12px',
          }}>上一页</button>
          <span style={{ lineHeight: '32px', fontSize: '12px', color: '#6b7280' }}>{page + 1} / {totalPages || 1}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} style={{
            padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db',
            background: page >= totalPages - 1 ? '#f9fafb' : 'white', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
            fontSize: '12px',
          }}>下一页</button>
        </div>

        {/* 公司笔记 DecisionBar — 深色工具栏卡片（嵌入式） */}
        {selectedCompanyId && (
          <div style={{
            marginTop: 12, borderRadius: 8, padding: '8px 12px',
            background: '#0f172a', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}>
            <DecisionBar entityId={selectedCompanyId} entityType="company" />
          </div>
        )}
      </div>
    );
  }

  // Desktop table layout
  return (
    <div style={{ padding: '16px 24px', overflow: 'auto', height: '100%', background: '#f8fafc', color: '#1f2937' }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
        {([
          { key: 'priority' as TabKey, label: '综合榜' },
          { key: 'tqs' as TabKey, label: '质量榜' },
          { key: 'mcs' as TabKey, label: '动量榜' },
        ]).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '8px 20px', borderRadius: '6px', fontSize: '14px',
            border: tab === t.key ? '2px solid #3b82f6' : '1px solid #d1d5db',
            background: tab === t.key ? '#eff6ff' : 'white', cursor: 'pointer', fontWeight: 600,
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>市场:</span>
          {(['all', 'A股', '港股通', '美股'] as MarketFilter[]).map(m => (
            <button key={m} onClick={() => setMarketFilter(m)} style={{
              padding: '3px 10px', borderRadius: '4px', fontSize: '12px',
              border: marketFilter === m ? '2px solid #3b82f6' : '1px solid #d1d5db',
              background: marketFilter === m ? '#eff6ff' : 'white', cursor: 'pointer',
            }}>
              {m === 'all' ? '全部' : m}
            </button>
          ))}
        </div>
        <div style={{ width: '1px', height: '18px', background: '#d1d5db' }} />
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>象限:</span>
          {(['all', '核心主攻', '质量等待', '弹性交易', '低优先级'] as QuadrantFilter[]).map(q => (
            <button key={q} onClick={() => setQuadrantFilter(q)} style={{
              padding: '3px 10px', borderRadius: '4px', fontSize: '12px',
              border: quadrantFilter === q ? '2px solid #10b981' : '1px solid #d1d5db',
              background: quadrantFilter === q ? '#ecfdf5' : 'white', cursor: 'pointer',
            }}>
              {q === 'all' ? '全部' : q}
            </button>
          ))}
        </div>
        <div style={{ width: '1px', height: '18px', background: '#d1d5db' }} />
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>模式:</span>
          {(['all', 'A', 'B', 'AB'] as ModeFilter[]).map(m => (
            <button key={m} onClick={() => setModeFilter(m)} style={{
              padding: '3px 10px', borderRadius: '4px', fontSize: '12px',
              border: modeFilter === m ? '2px solid #8b5cf6' : '1px solid #d1d5db',
              background: modeFilter === m ? '#f5f3ff' : 'white', cursor: 'pointer',
            }}>
              {m === 'all' ? '全部' : `${MODE_ICONS[m] || ''} ${m === 'A' ? 'A 财报验证' : m === 'B' ? 'B 稀缺爆发' : 'AB 混合'}`}
            </button>
          ))}
        </div>
        <div style={{ width: '1px', height: '18px', background: '#d1d5db' }} />
        <input
          type="text"
          placeholder="搜索公司名称..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '4px 10px', borderRadius: '4px', border: '1px solid #d1d5db',
            fontSize: '12px', width: '160px', outline: 'none',
          }}
        />
        <div style={{ marginLeft: 'auto', fontSize: '12px', color: '#6b7280' }}>
          共 {filtered.length} 家公司
        </div>
      </div>

      {/* Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #e5e7eb', position: 'sticky', top: 0, background: 'white' }}>
            <th style={{ textAlign: 'left', padding: '8px', width: '40px' }}>排名</th>
            <th style={{ textAlign: 'left', padding: '8px' }}>公司</th>
            <th style={{ textAlign: 'left', padding: '8px' }}>代码</th>
            <ThSortable field="priority" current={sortField} asc={sortAsc} onToggle={toggleSort}>
              综合P
            </ThSortable>
            <ThSortable field="tqs" current={sortField} asc={sortAsc} onToggle={toggleSort}>
              TQS
            </ThSortable>
            <ThSortable field="mcs" current={sortField} asc={sortAsc} onToggle={toggleSort}>
              MCS
            </ThSortable>
            <ThSortable field="ai" current={sortField} asc={sortAsc} onToggle={toggleSort}>
              AI关联
            </ThSortable>
            <th style={{ textAlign: 'center', padding: '8px' }}>模式</th>
            <th style={{ textAlign: 'center', padding: '8px' }}>象限</th>
            <ThSortable field="gain3y" current={sortField} asc={sortAsc} onToggle={toggleSort}>
              3年涨幅
            </ThSortable>
          </tr>
        </thead>
        <tbody>
          {paged.map((r, i) => {
            const rank = page * PAGE_SIZE + i + 1;
            const quadrant = r.ranking.quadrant || '';
            const modeLabel = r.ranking.mode_label || '';
            const gain3y = r.company?.price_gain_3y;
            const isSelected = selectedCompanyId === r.company_id;
            const isRelated = !!(relatedCompanyIds?.has(r.company_id));
            const rowBg = isSelected ? '#fef3c7' : isRelated ? '#fffbeb' : 'white';
            const rowOpacity = hasSelection && !isSelected && !isRelated ? 0.35 : 1;
            const rowBorderLeft = isSelected ? '4px solid #f59e0b' : isRelated ? '3px solid #fbbf24' : 'none';
            return (
              <tr key={r.company_id}
                onClick={() => selectCompany(selectedCompanyId === r.company_id ? null : r.company_id)}
                style={{
                  borderBottom: '1px solid #f3f4f6',
                  background: rowBg,
                  opacity: rowOpacity,
                  borderLeft: rowBorderLeft,
                  cursor: 'pointer',
                  transition: 'background 0.15s, opacity 0.15s',
                }}>
                <td style={{ padding: '6px 8px', fontWeight: 700, color: rank <= 3 ? '#3b82f6' : '#374151' }}>{rank}</td>
                <td style={{ padding: '6px 8px', fontWeight: isSelected ? 800 : 500 }}>{r.company?.company || r.company_id}</td>
                <td style={{ padding: '6px 8px', color: '#6b7280', fontFamily: 'monospace', fontSize: '11px' }}>{r.company?.ticker || ''}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: '#1f2937' }}>
                  {(r.ranking.priority_score ?? 0).toFixed(1)}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>
                  {(r.ranking.tqs_score ?? 0).toFixed(1)}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>
                  {(r.ranking.mcs_score ?? 0).toFixed(1)}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  {r.company?.ai_revenue_pct != null ? `${r.company.ai_revenue_pct.toFixed(0)}%` : '-'}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                  <span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '11px',
                    background: modeLabel === 'A' ? '#eff6ff' : modeLabel === 'B' ? '#f5f3ff' : modeLabel === 'AB' ? '#fefce8' : '#f3f4f6',
                    color: modeLabel === 'A' ? '#1e40af' : modeLabel === 'B' ? '#6d28d9' : modeLabel === 'AB' ? '#a16207' : '#6b7280',
                    fontWeight: 600,
                  }}>
                    {MODE_ICONS[modeLabel] || ''} {modeLabel || '-'}
                  </span>
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                  {quadrant && (
                    <span style={{
                      padding: '2px 8px', borderRadius: '4px', fontSize: '10px',
                      background: QUADRANT_COLORS[quadrant]?.bg || '#f3f4f6',
                      color: QUADRANT_COLORS[quadrant]?.text || '#6b7280',
                      fontWeight: 600,
                    }}>
                      {quadrant}
                    </span>
                  )}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: gainColor(gain3y) }}>
                  {formatGain(gain3y)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px', padding: '8px' }}>
        <button disabled={page === 0} onClick={() => setPage(page - 1)} style={{
          padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db',
          background: page === 0 ? '#f9fafb' : 'white', cursor: page === 0 ? 'not-allowed' : 'pointer',
          fontSize: '12px',
        }}>上一页</button>
        <span style={{ fontSize: '12px', color: '#6b7280' }}>
          第 {page + 1} / {totalPages || 1} 页
        </span>
        <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)} style={{
          padding: '6px 16px', borderRadius: '4px', border: '1px solid #d1d5db',
          background: page >= totalPages - 1 ? '#f9fafb' : 'white', cursor: page >= totalPages - 1 ? 'not-allowed' : 'pointer',
          fontSize: '12px',
        }}>下一页</button>
      </div>

      {/* 公司笔记 DecisionBar — 深色工具栏卡片（嵌入式） */}
      {selectedCompanyId && (
        <div style={{
          marginTop: 16, borderRadius: 8, padding: '8px 12px',
          background: '#0f172a', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          <DecisionBar entityId={selectedCompanyId} entityType="company" />
        </div>
      )}
    </div>
  );
}

// Sortable table header
function ThSortable({ field, current, asc, onToggle, children }: {
  field: SortField;
  current: SortField;
  asc: boolean;
  onToggle: (f: SortField) => void;
  children: React.ReactNode;
}) {
  const isActive = current === field;
  return (
    <th style={{ textAlign: 'right', padding: '8px', cursor: 'pointer', userSelect: 'none' }}
      onClick={() => onToggle(field)}>
      {children}
      <span style={{ fontSize: '10px', color: isActive ? '#3b82f6' : '#9ca3af' }}>
        {isActive ? (asc ? ' ↑' : ' ↓') : ' ↕'}
      </span>
    </th>
  );
}

// Mobile filter bar
function MobileFilterBar({ marketFilter, setMarketFilter, quadrantFilter, setQuadrantFilter, modeFilter, setModeFilter, search, setSearch }: {
  marketFilter: MarketFilter;
  setMarketFilter: (v: MarketFilter) => void;
  quadrantFilter: QuadrantFilter;
  setQuadrantFilter: (v: QuadrantFilter) => void;
  modeFilter: ModeFilter;
  setModeFilter: (v: ModeFilter) => void;
  search: string;
  setSearch: (v: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ marginBottom: '8px' }}>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="搜索公司..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db',
            fontSize: '13px', outline: 'none',
          }}
        />
        <button onClick={() => setExpanded(!expanded)} style={{
          padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db',
          background: 'white', cursor: 'pointer', fontSize: '12px',
        }}>
          筛选 {expanded ? '▲' : '▼'}
        </button>
      </div>
      {expanded && (
        <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>市场:</span>
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              {(['all', 'A股', '港股通', '美股'] as MarketFilter[]).map(m => (
                <button key={m} onClick={() => setMarketFilter(m)} style={{
                  padding: '4px 10px', borderRadius: '4px', fontSize: '11px',
                  border: marketFilter === m ? '2px solid #3b82f6' : '1px solid #d1d5db',
                  background: marketFilter === m ? '#eff6ff' : 'white', cursor: 'pointer',
                }}>
                  {m === 'all' ? '全部' : m}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>象限:</span>
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px', flexWrap: 'wrap' }}>
              {(['all', '核心主攻', '质量等待', '弹性交易', '低优先级'] as QuadrantFilter[]).map(q => (
                <button key={q} onClick={() => setQuadrantFilter(q)} style={{
                  padding: '4px 10px', borderRadius: '4px', fontSize: '11px',
                  border: quadrantFilter === q ? '2px solid #10b981' : '1px solid #d1d5db',
                  background: quadrantFilter === q ? '#ecfdf5' : 'white', cursor: 'pointer',
                }}>
                  {q === 'all' ? '全部' : q}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span style={{ fontSize: '11px', color: '#6b7280' }}>模式:</span>
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              {(['all', 'A', 'B', 'AB'] as ModeFilter[]).map(m => (
                <button key={m} onClick={() => setModeFilter(m)} style={{
                  padding: '4px 10px', borderRadius: '4px', fontSize: '11px',
                  border: modeFilter === m ? '2px solid #8b5cf6' : '1px solid #d1d5db',
                  background: modeFilter === m ? '#f5f3ff' : 'white', cursor: 'pointer',
                }}>
                  {m === 'all' ? '全部' : `${MODE_ICONS[m] || ''} ${m}`}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
